import { forEach, get, head, isEmpty, last, set } from 'lodash';
import { ICustomerPaymentMethod } from '../data/api/models';
import { differenceInDays, isBefore, isEqual, parse, startOfDay } from 'date-fns';

interface IAdditional {
  premium?: number,
  coverLevel?: string
  errorMessage?: string
  key?: string
}

export default class Heap {
  public static track(name, properties): void {
    if (window.heap) {
      window.heap.track(name, properties);
    }
  }

  public static trackEvent = (eventName: string, application: any, type: string, additional: IAdditional = null) => {

    switch (type) {
      case 'quote':
        Heap.trackQuoteEvent(eventName, application, additional)
        break;
      case 'policy':
        Heap.trackPolicyEvent(eventName, application, additional)
        break;
    }
  }

  private static trackQuoteEvent = (eventName: string, application: any, additional: IAdditional) => {

    const premium = head(get(application, 'premiums'));
    const errorMessage = get(additional, 'errorMessage')
    let additionalOptions = '';
    let hasScreening = false;
    if (get(application, 'options.gadget_cover')) {
      additionalOptions += 'Gadget Cover '
    }

    if (get(application, 'options.excess_waiver')) {
      additionalOptions += 'Excess Waiver'
    }

    forEach(get(application, 'travellers'), (traveller) => {
      if (traveller.screening) {
        hasScreening = true;
      }
    });

    const props = {
      trv_quote_reference: get(application, 'quoteReference', undefined),
      trv_channel: get(application, 'channelName', undefined),
      trv_aggregator: get(application, 'channel.channelType', '') === 'AGG' ? get(application, 'channel.name', undefined) : undefined,
      trv_policy_type: get(application, 'tripType', undefined),
      trv_country: !isEmpty(get(application, 'destinations', [])) ? get(application, 'destinations').join(', ') : undefined,
      trv_region: get(application, 'region', undefined),
      trv_start_date: get(application, 'startDate', undefined),
      trv_group_type: get(application, 'groupType', undefined),
      trv_winter_sports: get(application, 'options.winter_sports', false) ? 'Y' : 'N',
      trv_cruise_cover: get(application, 'options.cruise_cover', false) ? 'Y' : 'N',
      trv_voucher_code: get(application, 'campaignCode', undefined),
      trv_selected_tier: get(premium, 'scheme.coverLevel'),
      trv_additional_covers: additionalOptions,
      trv_medical: hasScreening ? 'Y' : 'N',
      trv_premium: get(premium, 'gross', 0),
      trv_tracking: window?.__PTR_TRACKING__ || undefined,
      trv_age: get(premium, 'information.lead_traveller_age', undefined)
    }

    if (errorMessage) {
      set(props, 'trv_purchase_error', errorMessage);
    }

    if (window.__HEAP_LOG__) {
      console.log(`%cEvent Name: ${eventName}`, 'color: green');
      console.log(props)
      console.log(application)
    }

    Heap.track(eventName, props);
  }

  private static trackPolicyEvent = (eventName: string, application: any, additional: any) => {

    let totalPremium;
    const totalPremiumValue = get(application, 'totalPremiumValue');
    const premium = last(get(application, 'premiums', null)) || get(additional, 'premium', null);

    if (totalPremiumValue) {
      totalPremium = totalPremiumValue;
    } else if (get(additional, 'premium')) {
      totalPremium = get(get(additional, 'premium'), 'gross', 0)
    } else {
      totalPremium = get(last(get(application, 'premiums', [])), 'gross', 0)
    }

    const selectedTier = get(additional, 'coverLevel') ? get(additional, 'coverLevel') : get(last(get(application, 'premiums')), 'scheme.coverLevel')
    let additionalOptions = '';
    if (get(application, 'metadata.options.gadget_cover')) {
      additionalOptions += 'Gadget Cover '
    }

    if (get(application, 'metadata.options.excess_waiver')) {
      additionalOptions += 'Excess Waiver'
    }

    const props = {
      trv_policy_number: get(application, 'policyNumber', undefined),
      trv_channel: get(application, 'channelName', undefined),
      trv_aggregator: get(application, 'metadata.aggregator', '') ? get(application, 'metadata.aggregator.source', undefined) : undefined,
      trv_policy_type: get(application, 'metadata.scheme_type', undefined),
      trv_country: !isEmpty(get(application, 'metadata.destinations', [])) ? get(application, 'metadata.destinations').join(', ') : undefined,
      trv_region: get(application, 'metadata.region', undefined),
      trv_start_date: get(application, 'metadata.start_date', undefined),
      trv_group_type: get(application, 'metadata.traveller_group', undefined),
      trv_winter_sports: get(application, 'metadata.options.winter_sports', false) ? 'Y' : 'N',
      trv_cruise_cover: get(application, 'metadata.options.cruise_cover', false) ? 'Y' : 'N',
      trv_voucher_code: get(application, 'metadata.campaign_code', undefined),
      trv_selected_tier: selectedTier,
      trv_additional_covers: additionalOptions,
      trv_premium: totalPremium,
      trv_tracking: window?.__PTR_TRACKING__ || undefined,
      trv_age: get(premium, 'information.leadTravellerAge', get(premium, 'information.lead_traveller_age', undefined))
    }

    if (window.__HEAP_LOG__) {
      console.log(`%cEvent Name: ${eventName}`, 'color: green');
      console.log(props)
      console.log(application)
    }

    Heap.track(eventName, props);
  }

  public static handleCalculationReferrals = (quoteReference: string, isMedical: boolean, isUnderwriter: boolean) => {
    const eventName = 'ReferralBlock';

    const props = {
      trv_quote_reference: quoteReference,
      trv_medical_block: isMedical ? 'Y' : 'N',
      trv_underwriting_block: isUnderwriter ? 'Y' : 'N',
      trv_tracking: window?.__PTR_TRACKING__ || undefined
    }

    if (window.__HEAP_LOG__) {
      console.log(`%cEvent Name: ${eventName}`, 'color: green');
      console.log(props)
    }

    Heap.track('ReferralBlock', props);

  }

  public static trackActivePolicyEvent = (eventName, policy, marketingOptions, paymentMethod?: ICustomerPaymentMethod, premiumType: string = undefined) => {

    const totalPremiumValue = get(policy, 'totalPremiumValue', undefined);
    const hasCAndI = get(paymentMethod, 'permissions.consent', false) && get(paymentMethod, 'permissions.intent', false) ? 'C&I' : 'Neither'
    const selectedTier = get(last(get(policy, 'premiums')), 'scheme.coverLevel')
    const hasExpired = paymentMethod?.expiry && (
      isEqual(parse(paymentMethod.expiry, 'dd/MM/yyyy', new Date()), new Date())
      || isBefore(parse(paymentMethod.expiry, 'dd/MM/yyyy', new Date()), new Date())
    ) ? 'Y' : 'N';
    const enabledMarketing = get(marketingOptions, 'email') && get(marketingOptions, 'phone') && get(marketingOptions, 'post') && get(marketingOptions, 'sms') ? 'Yes' : 'No'
    const isAnnual = get(policy, 'metadata.scheme_type', '') === 'annual'
    const startDate = get(policy, 'commencesAt', '');
    const endDate = get(policy, 'expiresAt', '');
    const duration = Heap.getDuration(startDate, endDate);
    let hasScreening = false;
    const premium = last(get(policy, 'premiums'));

    forEach(get(policy, 'travellers'), (traveller) => {
      if (traveller.screening) {
        hasScreening = true;
      }
    });

    const props = {
      trv_policy_number: get(policy, 'policyNumber', undefined),
      trv_policy_type: get(policy, 'metadata.scheme_type', '') === 'single' ? 'ST' : 'AMT',
      trv_premium_overall: totalPremiumValue,
      trv_term_number: get(policy, 'termNumber', undefined),
      trv_channel: get(policy, 'channelName', undefined),
      trv_group_type: get(policy, 'metadata.traveller_group', undefined),
      trv_renewal_preference: get(policy, 'renewalStatus', undefined),
      trv_c_and_i_preference: hasCAndI,
      trv_tier: selectedTier,
      trv_expired_card: hasExpired,
      trv_winter_sports: get(policy, 'metadata.options.winter_sports', false) ? 'Y' : 'N',
      trv_cruise_cover: get(policy, 'metadata.options.cruise_cover', false) ? 'Y' : 'N',
      trv_gadget: get(policy, 'metadata.options.gadget_cover', false) ? 'Y' : 'N',
      trv_excess: get(policy, 'metadata.options.excess_waiver', false) ? 'Y' : 'N',
      trv_hazardous: !isEmpty(get(policy, 'metadata.options.hazardous_activities', [])) ? 'Y' : 'N',
      trv_trip_Extension: get(policy, 'metadata.max_trip_duration', false) ? 'Y' : 'N',
      trv_voucher_code: get(policy, 'metadata.campaign_code', undefined),
      trv_region: get(policy, 'metadata.region', undefined),
      trv_destination: !isEmpty(get(policy, 'metadata.destinations', [])) ? get(policy, 'metadata.destinations').join(', ') : undefined,
      trv_marketing_preference: enabledMarketing,
      trv_medical: hasScreening,
      trv_number_of_travellers: get(policy, 'travellers', []).length,
      trv_trip_duration: duration,
      trv_start_date: get(policy, 'commencesAt'),
      trv_time_until_start_date: differenceInDays(parse(startDate, 'dd/MM/yyyy', new Date()), startOfDay(new Date())),
      trv_time_until_renewal: isAnnual ? differenceInDays(parse(endDate, 'dd/MM/yyyy', new Date()), startOfDay(new Date())) : undefined,
      trv_premium_type: premiumType,
      trv_tracking: window?.__PTR_TRACKING__ || undefined,
      trv_age: get(premium, 'information.leadTravellerAge', undefined)
    }

    if (window.__HEAP_LOG__) {
      console.log(`%cEvent Name: ${eventName}`, 'color: green');
      console.log(props)
      console.log(`%cPolicy: ${get(policy, 'policyNumber', undefined)}`, 'color: blue');
      console.log(policy)
    }

    Heap.track(eventName, props);
  }

  public static trackPolicyDocumentEvent = (eventName, policy, marketingOptions, paymentMethod?: ICustomerPaymentMethod) => {
    const hasExpired = paymentMethod?.expiry && (
      isEqual(parse(paymentMethod.expiry, 'dd/MM/yyyy', new Date()), new Date())
      || isBefore(parse(paymentMethod.expiry, 'dd/MM/yyyy', new Date()), new Date())
    ) ? 'Y' : 'N';
    const enabledMarketing = get(marketingOptions, 'email') && get(marketingOptions, 'phone') && get(marketingOptions, 'post') && get(marketingOptions, 'sms') ? 'Yes' : 'No'
    const premium = last(get(policy, 'premiums'));

    const props = {
      trv_policy_number: get(policy, 'policyNumber', undefined),
      trv_policy_type: get(policy, 'metadata.scheme_type', '') === 'single' ? 'ST' : 'AMT',
      trv_channel: get(policy, 'channelName', undefined),
      trv_expired_card: hasExpired,
      trv_marketing_preference: enabledMarketing,
      trv_tracking: window?.__PTR_TRACKING__ || undefined,
      trv_age: get(premium, 'information.leadTravellerAge', undefined)
    }

    if (window.__HEAP_LOG__) {
      console.log(`%cEvent Name: ${eventName}`, 'color: green');
      console.log(props)
      console.log(`%cPolicy: ${get(policy, 'policyNumber', undefined)}`, 'color: blue');
      console.log(policy)
    }

    Heap.track(eventName, props);
  }


  private static getDuration = (startDate: string, endDate: string) => {
    return differenceInDays(parse(endDate, 'dd/MM/yyyy', new Date()), parse(startDate, 'dd/MM/yyyy', new Date()))
  }
}
